import type { Company, Plant, PlantKiln } from '../../types/entities';
import { isKilnActive, isPlantActive, todayIsoDate } from './kilnActivity';

/**
 * Bestandszahlen der Stammdatenseite (28-UI-SPEC.md Punkt 7, Sketch 008 `tally()`). Reine Funktion
 * ueber die drei bereits im Speicher liegenden Collections -- kein Aggregat-Endpunkt, kein
 * zusaetzlicher Request (Datenherkunft laut UI-SPEC: client-seitig aus den geladenen
 * Company-/Plant-/PlantKiln-Collections).
 */
export interface MasterDataTally {
  companies: number;
  plants: number;
  kilns: number;
  plantsWithoutKiln: number;
}

/**
 * `PlantKiln.plant` ist eine IRI (`/api/plants/{id}`), kein eingebettetes Plant-Objekt -- verifiziert
 * gegen `/api/plant_kilns` (siehe types/entities.ts). Robust gegen beide Formen, falls sich die
 * Serialisierung einmal aendert.
 */
function extractPlantId(plant: PlantKiln['plant']): number | null {
  if (plant == null) {
    return null;
  }
  if (typeof plant === 'string') {
    const match = /\/(\d+)$/.exec(plant);
    return match ? Number(match[1]) : null;
  }
  const embedded = plant as { id?: number };
  return embedded.id ?? null;
}

export function buildMasterDataTally(
  companies: Company[] = [],
  plants: Plant[] = [],
  kilns: PlantKiln[] = [],
  onDate: string = todayIsoDate()
): MasterDataTally {
  // D-14: "Werke" und "Werke ohne Ofen" zaehlen nur heute aktive Werke (dieselbe Regel wie bei den
  // Oefen, jetzt am Werk selbst). Die Unternehmenszahl bleibt unveraendert -- D-15 verlangt
  // ausdruecklich keine abgeleitete Unternehmens-Stilllegung.
  const activePlants = plants.filter((plant) => isPlantActive(plant, onDate));
  const activePlantIds = new Set(activePlants.map((plant) => plant.id));

  // Gezaehlt werden nur die heute laufenden Oefen (`isKilnActive`) eines heute aktiven Werks
  // (D-11): der Ofen eines stillgelegten Werks gilt abgeleitet als nicht mehr laufend, auch wenn
  // er selbst kein eigenes Datum traegt. "Werke ohne Ofen" meint entsprechend aktive Werke ohne
  // aktiven Ofen -- auch eines, dessen saemtliche Oefen stillgelegt sind, faellt darunter.
  const activeKilnList = kilns.filter((kiln) => {
    if (!isKilnActive(kiln, onDate)) {
      return false;
    }
    const plantId = extractPlantId(kiln.plant);
    return plantId !== null && activePlantIds.has(plantId);
  });

  const kilnCountByPlantId = new Map<number, number>();
  for (const kiln of activeKilnList) {
    const plantId = extractPlantId(kiln.plant);
    if (plantId === null) {
      continue;
    }
    kilnCountByPlantId.set(plantId, (kilnCountByPlantId.get(plantId) ?? 0) + 1);
  }

  const plantsWithoutKiln = activePlants.filter(
    (plant) => (kilnCountByPlantId.get(plant.id) ?? 0) === 0
  ).length;

  return {
    companies: companies.length,
    plants: activePlants.length,
    kilns: activeKilnList.length,
    plantsWithoutKiln,
  };
}
